import { useCallback, useEffect, useState } from "react";
import { Document, Encounter } from "../../shared/types";
import { apiGet } from "src/utils/api";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import ItemListModal, { ViewableItem, EmailContext } from "./ItemListModal";

const documentToViewableItem = (doc: Document): ViewableItem => ({
  name: doc.name,
  date: doc.date,
  status: doc.status,
  contentType: doc.attachment.contentType,
  binaryUrl: doc.attachment.url,
});

type DocumentsModalProps = {
  encounter: Encounter;
  onClose: () => void;
};

const DocumentsModal = ({ encounter, onClose }: DocumentsModalProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ documents, setDocuments ] = useState<Document[]>([]);
  const [ loading, setLoading ] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!organization || !patientId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await apiGet(`/api/${organization.id}/patients/${patientId}/documents/${encounter.id}`);
      const data = await response.json();

      if (!data.error) {
        setDocuments(data);
      } else {
        setDocuments([]);
      }
    } catch (_) {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [ organization, patientId, encounter.id ]);

  useEffect(() => {
    fetchDocuments();
  }, [ fetchDocuments ]);

  const items = documents.map(documentToViewableItem);

  const getEmailContext = (): EmailContext => ({
    type: encounter.type,
    date: encounter.date,
    author: encounter.practitioner?.name || "Unknown",
  });

  const header = (
    <h3 className="text-lg font-semibold text-gray-900 mb-4">
      {encounter.type} {encounter.date === 'Unknown date' ? '' : `(${new Date(encounter.date).toLocaleDateString()})`}
    </h3>
  );

  return (
    <ItemListModal
      title="Encounter Documents"
      items={items}
      loading={loading}
      loadingMessage="Loading Documents..."
      emptyMessage="No documents found for this encounter"
      onClose={onClose}
      header={header}
      getEmailContext={getEmailContext}
    />
  );
};

export default DocumentsModal;
